import { MED_COLORS, MS_PER_DAY } from '../constants.js';
import { DOSAGE_PARAMS, isWithinTimeWindow, matchesChild } from '../rules/dosing.js';
import { capitalizeMed, formatElapsed, formatTimePart } from '../lib/format.js';

// Standing 24-hour usage summary for both painkillers. Shown before anything is
// calculated or logged, because "how much has this child already had, and of
// what?" is the question a parent actually opens the app with — it used to be
// answerable only after pressing Calculate, and then only for the one medicine
// whose tab happened to be open.
//
// `pending` ({med, mg}) overlays the not-yet-logged dose as a lighter segment on
// its own medicine's bar, so the projected total is visible before registering.
// Renders nothing when the child has no doses in the window and nothing is
// pending — two empty bars are noise, not information.
export function DoseTracker24h({ darkMode, dosageHistory, selectedChild, weight, pending }) {
  if (!weight) return null;

  const stats = ['paracetamol', 'ibuprofen'].map(med => {
    const doses = dosageHistory.filter(e =>
      e.medication === med &&
      isWithinTimeWindow(e.timestamp, MS_PER_DAY) &&
      matchesChild(e, selectedChild, weight)
    );
    const usedMg = doses.reduce((sum, e) => sum + parseFloat(e.mgTotal || 0), 0);
    const maxDaily = weight * DOSAGE_PARAMS[med].maxDailyPerKg;
    const pendingMg = pending && pending.med === med ? pending.mg : 0;
    return {
      med, doses, usedMg, maxDaily, pendingMg,
      lastDose: doses[0] || null,
      percent: maxDaily > 0 ? (usedMg / maxDaily) * 100 : 0,
      pendingPercent: maxDaily > 0 ? (pendingMg / maxDaily) * 100 : 0,
    };
  });

  if (stats.every(s => s.doses.length === 0) && !pending) return null;

  return (
    <div className={`border rounded-lg p-3 mb-4 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
      <h3 className={`font-bold mb-2 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
        Last 24 Hours
      </h3>
      <div className="space-y-2">
        {stats.map(s => {
          const colors = MED_COLORS[s.med];
          const totalPercent = s.percent + s.pendingPercent;
          return (
            <div key={s.med}>
              <div className="flex justify-between items-baseline gap-2 mb-1">
                <span className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.bar}`} />
                  {capitalizeMed(s.med)}
                </span>
                <span className={`text-sm font-bold whitespace-nowrap ${
                  totalPercent > 100
                    ? 'text-red-500'
                    : totalPercent > 90
                    ? 'text-orange-500'
                    : darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {s.usedMg.toFixed(0)} / {s.maxDaily.toFixed(0)} mg
                </span>
              </div>
              <div className={`w-full h-3 rounded-full overflow-hidden flex ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div className={`h-full transition-all ${colors.bar}`}
                  style={{ width: `${Math.min(s.percent, 100)}%` }} />
                {s.pendingPercent > 0 && (
                  <div className={`h-full transition-all ${colors.pending}`}
                    style={{ width: `${Math.max(0, Math.min(s.pendingPercent, 100 - s.percent))}%` }} />
                )}
              </div>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {s.doses.length === 0
                  ? 'None in the last 24 h'
                  : `${s.doses.length} dose${s.doses.length > 1 ? 's' : ''} · last one ${formatElapsed(s.lastDose.timestamp)} ago (${formatTimePart(s.lastDose.timestamp)}) · ${Math.max(0, s.maxDaily - s.usedMg).toFixed(0)} mg left`}
                {s.pendingMg > 0 && ` · +${s.pendingMg.toFixed(0)} mg with this dose`}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
